import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { unwrap } from '@/api/envelope';
import {
  listMembers,
  listSupporters,
  readSupporterSummary,
  type SupporterRow,
  type SupporterSummary,
} from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import type { SortDirection } from '@/components/ui/DataTable';
import { SEARCH_MAX_LENGTH } from '@/components/ui/FilterBar';

import { listKey, summaryKey } from './keys';
import { SORT_COLUMN, type SupportersParams } from './useSupportersParams';

/**
 * Which organization's supporters the page shows: the one staff are viewing,
 * or the signed-in person's own. Every key carries it (see keys.ts).
 */
export function useSupportersOrganizationId(): string | null {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  return viewing ? viewing.id : (user?.organizationId ?? null);
}

/** What one list request sends; the list key carries exactly this. */
export interface SupportersQuery {
  sort: typeof SORT_COLUMN;
  direction: SortDirection;
  page: number;
  limit: number;
  search?: string;
}

/**
 * The request for a view: always the page and the page size (P14, backend
 * D13) and the one sortable column with its direction; the search trimmed,
 * capped at the backend's bound (backend D12, P10), and left out when blank.
 */
export function listQueryFor(params: SupportersParams): SupportersQuery {
  const search = params.search.trim().slice(0, SEARCH_MAX_LENGTH);
  return {
    sort: SORT_COLUMN,
    direction: params.direction,
    page: params.page,
    limit: params.limit,
    ...(search ? { search } : {}),
  };
}

export interface SupportersPage {
  /** The generated row, unchanged: the backend's registry decides its keys (I1). */
  rows: SupporterRow[];
  pagination: { total: number; page: number; limit: number; hasMore: boolean };
}

/**
 * One page of the followed or the invited list. Each selection reads its own
 * route (P1), so paging, search and sort are the server's and exact at any
 * depth. The previous page stays on screen while the next loads, so the
 * pager keeps its place - but only within one organization: after a switch
 * the table shows its loading rows, never the last organization's (P9, I3).
 */
export function useSupporterList(params: SupportersParams) {
  const organizationId = useSupportersOrganizationId();
  const query = listQueryFor(params);
  return useQuery({
    queryKey: listKey(organizationId, params.type, query),
    enabled: organizationId !== null,
    queryFn: async (): Promise<SupportersPage> => {
      const read = params.type === 'invited' ? listMembers : listSupporters;
      const reply = await read({ query, throwOnError: true });
      const data = unwrap(reply.data);
      return {
        rows: data.items,
        pagination: {
          total: data.pagination.total,
          page: data.pagination.page,
          limit: data.pagination.limit,
          hasMore: data.pagination.has_more,
        },
      };
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === organizationId
        ? keepPreviousData(previous)
        : undefined,
  });
}

/**
 * The totals behind the cards, read on their own (P8): they never follow a
 * search or a list's `pagination.total` (I2, backend D9).
 */
export function useSupporterSummary() {
  const organizationId = useSupportersOrganizationId();
  return useQuery({
    queryKey: summaryKey(organizationId),
    enabled: organizationId !== null,
    queryFn: async (): Promise<SupporterSummary> => {
      const reply = await readSupporterSummary({ throwOnError: true });
      return unwrap(reply.data);
    },
  });
}
